/**
 * Encodes the comparison video in the browser.
 *
 * Frames come from the same `drawFrame` the previews use, encoded with
 * mediabunny (already this project's exporter) straight to a downloadable file.
 * Nothing is uploaded and nothing touches the editor.
 */
import {
	AudioBufferSource,
	BufferTarget,
	CanvasSource,
	Mp4OutputFormat,
	Output,
	QUALITY_HIGH,
	WebMOutputFormat,
} from "mediabunny";
import { drawFrame } from "./draw";
import type { Scene } from "./scene";

export type VideoFormat = "mp4" | "webm";

const FPS = 30;

/**
 * Flat vector art and text compress far better than camera footage, so this
 * sits below the editor's screen-recording reference and still stays crisp.
 */
const REFERENCE_BITRATE = 3_500_000;
const REFERENCE_PIXELS = 1920 * 1080;

function videoBitrate({
	width,
	height,
	codec,
}: {
	width: number;
	height: number;
	codec: "avc" | "vp9";
}): number {
	const scaled = REFERENCE_BITRATE * ((width * height) / REFERENCE_PIXELS);
	const efficiency = codec === "vp9" ? 0.65 : 1;
	return Math.round(
		Math.min(8_000_000, Math.max(1_500_000, scaled * efficiency)),
	);
}

export async function decodeNarration({
	audioBlob,
}: {
	audioBlob: Blob;
}): Promise<AudioBuffer> {
	const context = new AudioContext();
	try {
		return await context.decodeAudioData(await audioBlob.arrayBuffer());
	} finally {
		void context.close();
	}
}

export async function exportComparisonVideo({
	scene,
	audioBuffer,
	format,
	onProgress,
	signal,
}: {
	scene: Scene;
	audioBuffer: AudioBuffer;
	format: VideoFormat;
	onProgress?: ({ progress }: { progress: number }) => void;
	signal?: AbortSignal;
}): Promise<Blob> {
	// WebCodecs is what does the encoding; Firefox and older Safari don't have
	// it, and a missing global would otherwise surface as a cryptic mediabunny
	// error halfway through.
	if (typeof VideoEncoder === "undefined") {
		throw new Error(
			"This browser can't encode video. Open the page in Chrome or Edge to download.",
		);
	}

	const { width, height } = scene.layout.canvas;

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d", { alpha: false });
	if (!ctx) throw new Error("This browser can't render the video.");

	const videoCodec = format === "webm" ? "vp9" : "avc";
	const output = new Output({
		format: format === "webm" ? new WebMOutputFormat() : new Mp4OutputFormat(),
		target: new BufferTarget(),
	});

	const videoSource = new CanvasSource(canvas, {
		codec: videoCodec,
		bitrate: videoBitrate({ width, height, codec: videoCodec }),
	});
	output.addVideoTrack(videoSource, { frameRate: FPS });

	const audioSource = new AudioBufferSource({
		codec: await pickAudioCodec({ format, audioBuffer }),
		bitrate: QUALITY_HIGH,
	});
	output.addAudioTrack(audioSource);

	await output.start();
	await audioSource.add(audioBuffer);
	audioSource.close();

	const frameCount = Math.max(1, Math.ceil((scene.durationMs / 1000) * FPS));

	for (let frame = 0; frame < frameCount; frame++) {
		if (signal?.aborted) {
			await output.cancel();
			throw new DOMException("Export cancelled", "AbortError");
		}

		const timeSeconds = frame / FPS;
		drawFrame({ ctx, scene, timeMs: timeSeconds * 1000 });
		await videoSource.add(timeSeconds, 1 / FPS);

		onProgress?.({ progress: (frame + 1) / frameCount });
		// Yield periodically so the tab stays responsive during a long encode.
		if (frame % 15 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
	}

	videoSource.close();
	await output.finalize();

	const buffer = output.target.buffer;
	if (!buffer) throw new Error("The encoder returned nothing.");

	return new Blob([buffer], {
		type: format === "webm" ? "video/webm" : "video/mp4",
	});
}

/** MP4 wants AAC, but not every browser can encode it — fall back to Opus. */
async function pickAudioCodec({
	format,
	audioBuffer,
}: {
	format: VideoFormat;
	audioBuffer: AudioBuffer;
}): Promise<"aac" | "opus"> {
	if (format === "webm") return "opus";
	if (typeof AudioEncoder === "undefined") return "opus";

	try {
		const { supported } = await AudioEncoder.isConfigSupported({
			codec: "mp4a.40.2",
			sampleRate: audioBuffer.sampleRate,
			numberOfChannels: audioBuffer.numberOfChannels,
			bitrate: 192_000,
		});
		return supported ? "aac" : "opus";
	} catch {
		return "opus";
	}
}

export function downloadBlob({
	blob,
	filename,
}: {
	blob: Blob;
	filename: string;
}): void {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	// Revoke late: Safari aborts the download if the URL dies too soon.
	setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
